import { useInstantSearch, useHits, usePagination } from 'react-instantsearch'
import { SimpleGrid, Spacer, Stack } from '@backyard-ui/core'
import { useEffect, useState } from 'react'

import { useUserResources } from 'shared/hooks/useUserResources'
import { NewProductThumb } from 'shared/components/NewProductThumb'
import { Pagination } from 'shared/components/Pagination'

import { setInsightsInfo } from 'entry-key/utils/analytics'
import { GridContent } from 'entry-key/pages/entry/components/GridContent'

import { TabSkeleton } from './TabSkeleton'
import { normalize, normalizeAssistedSale } from '../../Content/Content.utils'
import { NoServicesFound } from './NoServicesFound'
import { IS_ON_MOBILE_APP } from '../../../constants'
import { onProductClickDataLayer } from '../../Content/Content.tracker'

const IS_CATEGORY = false

export interface ServicesTabContentInnerProps {
  expectedQuery: string
  serviceIndexName?: string
  isActive: boolean
  isAssistedSale: boolean
  storeSlug?: string
}

export const ServicesTabContentInner = ({
  expectedQuery,
  serviceIndexName,
  isActive,
  isAssistedSale,
  storeSlug
}: ServicesTabContentInnerProps) => {
  const [showEmptyState, setShowEmptyState] = useState(false)

  const { userResources } = useUserResources()

  const { status, results } = useInstantSearch()
  const { nbPages, currentRefinement, refine } = usePagination()
  const { items, sendEvent } = useHits({ escapeHTML: false })

  const showRetailMedia = window.env.newTail.enabled
  const hasReceivedResponse = results && typeof results.nbHits === 'number'
  const hasResults = results?.nbHits && results.nbHits > 0
  const isEmpty = hasReceivedResponse && (!hasResults || items.length === 0)
  const isLoading = status === 'loading' || status === 'stalled'
  const isWaitingForResults = (isLoading && !hasResults) || items.length === 0
  const region = userResources?.region ?? ''

  useEffect(() => {
    if (currentRefinement > 0) {
      window.scrollTo({ top: 0, behavior: 'instant' })
    }
  }, [currentRefinement])

  useEffect(() => {
    if (!isEmpty) {
      setShowEmptyState(false)
      return
    }

    const timer = setTimeout(() => setShowEmptyState(true), 500)
    return () => clearTimeout(timer)
  }, [isEmpty])

  const setPage = (page: number) => {
    refine(page - 1)
  }

  const onServiceClick = (service: unknown, index: number) => {
    if (!isActive) return

    setInsightsInfo(service, region, serviceIndexName)
    sendEvent('click', service, 'serviceListClick')
    onProductClickDataLayer({
      index,
      region,
      product: service,
      page: currentRefinement + 1,
      term: expectedQuery ?? '',
      inWishlist: false
    })
  }

  if (showEmptyState) {
    return <NoServicesFound />
  }

  if (isWaitingForResults) {
    return <TabSkeleton />
  }

  const services = items.map(item => {
    const normalized =
      isAssistedSale && storeSlug
        ? normalizeAssistedSale(item as Record<string, unknown>, storeSlug)
        : normalize(item, region, IS_CATEGORY)
    return {
      ...item,
      ...normalized,
      id: Number(normalized.id || item.id || item.objectID)
    }
  })

  return (
    <GridContent
      showRetailMedia={showRetailMedia}
      headerProps={{ contentType: 'services' }}>
      <Stack align="center" direction="column">
        <SimpleGrid
          columns={{
            initial: 2,
            sm: 3,
            md: 4
          }}>
          {services.map((service, index) => (
            <NewProductThumb
              key={service.objectID}
              partnerName="algolia"
              onClick={() => onServiceClick(service, index)}
              product={service}
              fullWidth
              index={index}
              itemList={`entry-key-${expectedQuery}`}
            />
          ))}
        </SimpleGrid>

        {nbPages > 1 && (
          <Pagination
            currentPage={currentRefinement + 1}
            totalPages={nbPages}
            setPage={setPage}
            pageCount={nbPages}
            page={currentRefinement + 1}
          />
        )}

        {IS_ON_MOBILE_APP && (
          <Spacer
            axis="vertical"
            size="10"
            data-testid="mobile-context-bottom-spacer"
          />
        )}
      </Stack>
    </GridContent>
  )
}
